import React from 'react';
import Button from 'components/Buttons/Button';
import DragHandleHorizontal from 'components/Icons/icons/DragHandleVertical';
import tw from 'twin.macro';
import Popover from 'components/Popover';
import { TagForm } from 'components/Form/TagPicker/TagForm';
import { Tag } from 'types/event';
import { Classroom } from 'types/classroom';
import useUpdateTag from 'queries/tags/useUpdateTag';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { useDragListeners } from './SortableItem';
import { TagItem } from 'components/Form/TagPicker/TagItem';
import useRemoveTags from 'queries/tags/useRemoveTag';
import { PencilIcon as Pencil } from '@heroicons/react/24/outline';
import { listDefaultTags } from 'containers/MesAnneesScolaires/School/Tags/listDefaultTags';
import { ColorPaletteConfig } from 'containers/App/context/useColorPalette';

interface Props {
  onAddTagToClassroom: (p: {
    tagId: string;
    classroomId: string;
  }) => Promise<any>;
  onChange: (v?: Tag) => void;
  value: Tag;
  currentClassrooms: Classroom[];
  setCurrentTag: (v: string | undefined) => void;
  currentTagOpen: string | undefined;
  disableEdit?: boolean;
  colorPalette: ColorPaletteConfig;
}

function getClassroomsThatDoNotHaveThatTag(
  option: Tag,
  classrooms: Classroom[],
): Classroom[] {
  return classrooms.filter(cl => !cl.tags.map(t => t.id).includes(option.id));
}

export function TagLine(props: Props) {
  const {
    onAddTagToClassroom,
    setCurrentTag,
    currentTagOpen,
    onChange,
    currentClassrooms,
    value,
    disableEdit,
    colorPalette,
  } = props;
  const dragLi = useDragListeners();
  const { schoolyear, school } = useSchoolSchoolyear();
  const updateTag = useUpdateTag({
    schoolyearId: schoolyear.id,
    schoolId: school.id,
  });
  const removeTag = useRemoveTags({
    schoolyearId: schoolyear.id,
    schoolId: school.id,
  });
  return (
    <div
      onClick={async () => {
        const optionBelongToClassrooms = getClassroomsThatDoNotHaveThatTag(
          value,
          currentClassrooms,
        );
        onChange(value);
        if (optionBelongToClassrooms.length > 0 && onAddTagToClassroom) {
          await Promise.all(
            optionBelongToClassrooms.map(cl =>
              onAddTagToClassroom({
                tagId: value.id,
                classroomId: cl.id,
              }),
            ),
          );
        }
      }}
      data-testid={`Tag ${value.title}`}
      css={[
        tw`flex items-center hover:bg-neutral-supershy cursor-pointer rounded-lg`,
        disableEdit && tw`mb-1.5`,
      ]}
      className="group"
    >
      {!disableEdit && (
        <div onClick={e => e.stopPropagation()}>
          <div {...(dragLi?.listeners || {})}>
            <Button minimal icon={DragHandleHorizontal as any} />
          </div>
        </div>
      )}
      <div css={[tw`flex flex-grow min-w-0 mr-1`]}>
        <TagItem value={value} />
      </div>
      {!disableEdit && (
        <div onClick={e => e.stopPropagation()}>
          <Popover
            portal
            fill
            placement="bottom-end"
            onClickOutside={e => {
              // Cas où on click sur le bouton confirmDelete dans la portal de confirmation
              if (!e.target || (e.target as HTMLElement).tagName !== 'BUTTON') {
                setCurrentTag(undefined);
              }
            }}
            open={currentTagOpen === value.id}
          >
            <Popover.Button
              tw="shrink-0"
              minimal
              small
              onClick={() => {
                if (currentTagOpen) {
                  setCurrentTag(undefined);
                  return;
                }
                setCurrentTag(value.id);
              }}
              data-testid="modify-tag-button"
              icon={Pencil}
            />

            <Popover.Panel size="xl">
              <div tw="p-4">
                <TagForm
                  onClose={() => setCurrentTag(undefined)}
                  onDelete={() => removeTag(value.id)}
                  options={listDefaultTags(
                    currentClassrooms.flatMap(c => c.cycle),
                  )}
                  onSubmit={t =>
                    updateTag({
                      ...value,
                      ...t,
                      classrooms: currentClassrooms.map(cl => ({ id: cl.id })),
                    })
                  }
                  initialValues={value}
                  tagId={value.id}
                  colorPalette={colorPalette}
                />
              </div>
            </Popover.Panel>
          </Popover>
        </div>
      )}
    </div>
  );
}
